import React, { useEffect, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import { authUtils, siteApi } from "@/lib/api";
import { showErrorToast, showSuccessToast } from "@/utils/toastUtils";
import AuthNoticeModal from "./AuthNoticeModal";

const trimValue = (value) => String(value ?? "").trim();

const getInitialReferralCode = () => {
  if (typeof window === "undefined") return "";

  const params = new URLSearchParams(window.location.search);
  return (
    params.get("referral_code") ||
    params.get("referral") ||
    params.get("ref") ||
    params.get("code") ||
    sessionStorage.getItem("affiliate_referral_code") ||
    localStorage.getItem("affiliate_referral_code") ||
    ""
  );
};

const getInitialValues = () => ({
  user_unique_id: "",
  nick_name: "",
  password: "",
  verify_password: "",
  exchange_password: "",
  usdt_wallet_address: "",
  telegram_id: "",
  referral_code: getInitialReferralCode(),
});

const getSignupMessage = (message, fallback = "처리 중 오류가 발생했습니다.") => {
  const text = trimValue(message);
  if (!text) return fallback;

  const lowerText = text.toLowerCase();
  if (text === "Registration successfull" || text === "Registration successful") return "무기명 가입이 완료되었습니다.";
  if (text === "Success" || text === "Available") return "사용 가능합니다.";
  if (lowerText.includes("nickname") && (lowerText.includes("exist") || lowerText.includes("duplicate"))) {
    return "이미 사용 중인 닉네임입니다.";
  }
  if ((lowerText.includes("id") || lowerText.includes("user")) && (lowerText.includes("exist") || lowerText.includes("duplicate"))) {
    return "이미 사용 중인 아이디입니다.";
  }
  if (lowerText.includes("wallet") || lowerText.includes("usdt")) return "USDT 지갑주소를 확인해주세요.";
  if (lowerText.includes("referral") || lowerText.includes("affiliate")) return "가입코드를 확인해주세요.";
  if (lowerText.includes("telegram")) return "텔레그램 ID를 확인해주세요.";
  return text;
};

const getAnonymousSignupAlertMessage = (values, checks) => {
  if (!trimValue(values.user_unique_id)) return "아이디를 입력해주세요.";
  if (!checks.user_unique_id.checked || checks.user_unique_id.isDuplicate) return "아이디 중복확인을 해주세요.";
  if (!trimValue(values.nick_name)) return "닉네임을 입력해주세요.";
  if (!checks.nick_name.checked || checks.nick_name.isDuplicate) return "닉네임 중복확인을 해주세요.";
  if (!trimValue(values.password)) return "비밀번호를 입력해주세요.";
  if (!trimValue(values.verify_password)) return "비밀번호 확인을 입력해주세요.";
  if (values.password !== values.verify_password) return "비밀번호가 일치하지 않습니다.";
  if (!trimValue(values.exchange_password)) return "출금 비밀번호를 입력해주세요.";
  if (!trimValue(values.usdt_wallet_address)) return "USDT 지갑주소를 입력해주세요.";
  if (!checks.usdt_wallet_address.checked || checks.usdt_wallet_address.isDuplicate) return "USDT 중복확인을 해주세요.";
  if (!trimValue(values.telegram_id)) return "텔레그램 ID를 입력해주세요.";
  if (!trimValue(values.referral_code)) return "가입코드를 입력해주세요.";
  return "";
};

const duplicatePayloadByField = {
  user_unique_id: (value) => ({ key_type: "USER", user_unique_id: value }),
  nick_name: (value) => ({ key_type: "USER", nick_name: value }),
  usdt_wallet_address: (value) => ({ key_type: "USER_INFO", usdt_wallet_address: value }),
};

const AnonymousRegister = ({ onSuccess, onSwitchToLogin }) => {
  const { fetchUserProfile } = useAuth();
  const [values, setValues] = useState(() => getInitialValues());
  const [noticeMessage, setNoticeMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateStatus, setDuplicateStatus] = useState({
    user_unique_id: { checked: false, isDuplicate: false, isChecking: false },
    nick_name: { checked: false, isDuplicate: false, isChecking: false },
    usdt_wallet_address: { checked: false, isDuplicate: false, isChecking: false },
  });

  const updateValue = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (duplicatePayloadByField[name]) {
      setDuplicateStatus((prev) => ({
        ...prev,
        [name]: { checked: false, isDuplicate: false, isChecking: false },
      }));
    }
  };

  const verifyDuplicate = async (fieldName) => {
    const value = trimValue(values[fieldName]);
    const emptyMessageByField = {
      user_unique_id: "아이디를 입력해주세요.",
      nick_name: "닉네임을 입력해주세요.",
      usdt_wallet_address: "USDT 지갑주소를 입력해주세요.",
    };

    if (!value) {
      setNoticeMessage(emptyMessageByField[fieldName] || "입력값을 확인해주세요.");
      return;
    }

    setDuplicateStatus((prev) => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], isChecking: true },
    }));

    const response = await siteApi.verifyDuplicate(duplicatePayloadByField[fieldName](value));

    if (response.success) {
      setDuplicateStatus((prev) => ({
        ...prev,
        [fieldName]: { checked: true, isDuplicate: false, isChecking: false },
      }));
      showSuccessToast("사용 가능합니다.");
      return;
    }

    setDuplicateStatus((prev) => ({
      ...prev,
      [fieldName]: { checked: true, isDuplicate: true, isChecking: false },
    }));
    setNoticeMessage(getSignupMessage(response.error, "이미 사용 중입니다."));
  };

  const getDuplicateButtonLabel = (fieldName) => {
    const status = duplicateStatus[fieldName];
    if (status.isChecking) return "확인 중";
    if (!status.checked) return "중복확인";
    return status.isDuplicate ? "중복" : "사용가능";
  };

  const getDuplicateButtonClass = (fieldName) => {
    const status = duplicateStatus[fieldName];
    if (!status.checked) return "";
    return status.isDuplicate ? "error" : "success";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const alertMessage = getAnonymousSignupAlertMessage(values, duplicateStatus);
    if (alertMessage) {
      setNoticeMessage(alertMessage);
      return;
    }

    const payload = {
      user_unique_id: trimValue(values.user_unique_id),
      nick_name: trimValue(values.nick_name),
      password: values.password,
      exchange_password: trimValue(values.exchange_password),
      referral_code: trimValue(values.referral_code),
      usdt_wallet_address: trimValue(values.usdt_wallet_address),
      telegram_id: trimValue(values.telegram_id),
    };

    try {
      setIsSubmitting(true);
      const response = await authUtils.anonymousRegister(payload);
      if (!response.success) {
        const message = getSignupMessage(response.error, "무기명 가입에 실패했습니다.");
        setNoticeMessage(message);
        showErrorToast(message);
        return;
      }

      showSuccessToast(getSignupMessage(response.message, "무기명 가입이 완료되었습니다."));
      if (response.token) {
        await fetchUserProfile();
      }
      onSuccess?.();
    } catch (error) {
      const message = getSignupMessage(error.message, "무기명 가입에 실패했습니다.");
      setNoticeMessage(message);
      showErrorToast(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setValues((prev) => ({ ...prev, referral_code: prev.referral_code || getInitialReferralCode() }));
  }, []);

  return (
    <form className="auth-form is-anonymous" onSubmit={handleSubmit}>
      <AuthNoticeModal message={noticeMessage} onClose={() => setNoticeMessage("")} />

      <div className="auth-form-grid">
        <div className="form-group">
          <label htmlFor="anonymous_user_unique_id" className="form-label">
            아이디 <span className="required">*</span>
          </label>
          <div className="auth-field-row">
            <input
              type="text"
              id="anonymous_user_unique_id"
              name="user_unique_id"
              className="form-input"
              value={values.user_unique_id}
              onChange={updateValue}
              placeholder="영문 소문자/숫자"
              autoComplete="username"
            />
            <button
              type="button"
              className={`auth-inline-button ${getDuplicateButtonClass("user_unique_id")}`}
              onClick={() => verifyDuplicate("user_unique_id")}
              disabled={duplicateStatus.user_unique_id.isChecking}
            >
              {getDuplicateButtonLabel("user_unique_id")}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="anonymous_nick_name" className="form-label">
            닉네임 <span className="required">*</span>
          </label>
          <div className="auth-field-row">
            <input
              type="text"
              id="anonymous_nick_name"
              name="nick_name"
              className="form-input"
              value={values.nick_name}
              onChange={updateValue}
              placeholder="닉네임"
              autoComplete="nickname"
            />
            <button
              type="button"
              className={`auth-inline-button ${getDuplicateButtonClass("nick_name")}`}
              onClick={() => verifyDuplicate("nick_name")}
              disabled={duplicateStatus.nick_name.isChecking}
            >
              {getDuplicateButtonLabel("nick_name")}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="anonymous_password" className="form-label">
            비밀번호 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="anonymous_password"
            name="password"
            className="form-input"
            value={values.password}
            onChange={updateValue}
            placeholder="비밀번호"
            autoComplete="new-password"
          />
        </div>

        <div className="form-group">
          <label htmlFor="anonymous_verify_password" className="form-label">
            비밀번호 확인 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="anonymous_verify_password"
            name="verify_password"
            className="form-input"
            value={values.verify_password}
            onChange={updateValue}
            placeholder="비밀번호 재입력"
            autoComplete="new-password"
          />
        </div>

        <div className="form-group">
          <label htmlFor="anonymous_exchange_password" className="form-label">
            출금 비밀번호 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="anonymous_exchange_password"
            name="exchange_password"
            className="form-input"
            value={values.exchange_password}
            onChange={updateValue}
            placeholder="숫자 4자리"
            inputMode="numeric"
            maxLength={4}
            autoComplete="new-password"
          />
        </div>

        <div className="form-group">
          <label htmlFor="anonymous_telegram_id" className="form-label">
            텔레그램 ID <span className="required">*</span>
          </label>
          <input
            type="text"
            id="anonymous_telegram_id"
            name="telegram_id"
            className="form-input"
            value={values.telegram_id}
            onChange={updateValue}
            placeholder="@username"
          />
        </div>

        <div className="form-group full-span">
          <label htmlFor="anonymous_usdt_wallet_address" className="form-label">
            USDT 지갑주소 <span className="required">*</span>
          </label>
          <div className="auth-field-row">
            <input
              type="text"
              id="anonymous_usdt_wallet_address"
              name="usdt_wallet_address"
              className="form-input"
              value={values.usdt_wallet_address}
              onChange={updateValue}
              placeholder="TRC20 지갑주소"
            />
            <button
              type="button"
              className={`auth-inline-button ${getDuplicateButtonClass("usdt_wallet_address")}`}
              onClick={() => verifyDuplicate("usdt_wallet_address")}
              disabled={duplicateStatus.usdt_wallet_address.isChecking}
            >
              {getDuplicateButtonLabel("usdt_wallet_address")}
            </button>
          </div>
        </div>

        <div className="form-group full-span">
          <label htmlFor="anonymous_referral_code" className="form-label">
            가입코드 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="anonymous_referral_code"
            name="referral_code"
            className="form-input"
            value={values.referral_code}
            onChange={updateValue}
            placeholder="가입코드"
          />
        </div>
      </div>

      <button type="submit" className="register-button" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <span className="auth-spinner" aria-hidden="true" />
            가입 중
          </>
        ) : (
          "무기명 가입"
        )}
      </button>

      <button type="button" className="auth-switch-button" onClick={onSwitchToLogin}>
        이미 계정이 있으신가요? 로그인
      </button>
    </form>
  );
};

export default AnonymousRegister;
